import type { SelectProps } from 'antd'
import { Button, Form, Select, type FormProps, Input, Modal } from 'antd'
import React, { useState, useEffect } from 'react'

import { useNotifications } from '@store/notifications'

import type { FeatureFlag, FeatureFlagRule } from '../../../../types'

type ModalProps = {
    featureFlags: FeatureFlag[] | undefined
    isOpen: boolean
    onClose: () => void
    onAddRule: (formValues: Partial<FeatureFlagRule>) => Promise<void>
}

const allFieldOptions = {
    user: [
        { label: 'ID', value: 'id' },
        { label: 'Email', value: 'email' }
    ],
    subscription: [{ label: 'id', value: 'id' }],
    school: [
        { label: 'id', value: 'id' },
        { label: 'name', value: 'name' },
        { label: 'country', value: 'country' }
    ],
    product: [
        { label: 'id', value: 'id' },
        { label: 'key', value: 'key' }
    ]
}

export const AddFeatureFlagRuleModal: React.FC<ModalProps> = ({ featureFlags, isOpen, onClose, onAddRule }) => {
    const [featureFlagOptions, setFeatureFlagOptions] = useState<SelectProps[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [fieldOptions, setFieldOptions] = useState<SelectProps[]>([])
    const { showNotification } = useNotifications()

    useEffect(() => {
        const prepFeatureFlags = async () => {
            if (featureFlags) {
                const options = featureFlags.map(flag => ({ label: flag.key, value: flag.id }))
                setFeatureFlagOptions(options)
            }
        }
        prepFeatureFlags()
    }, [isOpen])

    const onConfirm: FormProps<any>['onFinish'] = async formValues => {
        setIsLoading(true)
        try {
            await onAddRule(formValues)
            showNotification({
                title: 'Feature flag rule has been added'
            })

            onClose()
        } catch (err: any) {
            showNotification({
                type: 'error',
                title: 'Failed to add feature flag rule',
                description: err.toString()
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleModelChange = (value: string) => {
        const newOptions = allFieldOptions[value as keyof typeof allFieldOptions]
        setFieldOptions(newOptions)
    }

    return (
        <Modal open={isOpen} footer={[]} onCancel={onClose}>
            <Form
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                layout="horizontal"
                disabled={false}
                onFinish={onConfirm}
            >
                <h3>Add New Rule</h3>
                <Form.Item label="Feature Flag" name="featureFlagId">
                    <Select style={{ width: 200 }} options={featureFlagOptions} />
                </Form.Item>
                <Form.Item label="Model" name="model">
                    <Select
                        style={{ width: 200 }}
                        onChange={handleModelChange}
                        options={[
                            { label: 'User', value: 'user' },
                            { label: 'Subscription', value: 'subscription' },
                            { label: 'School', value: 'school' },
                            { label: 'Product', value: 'product' }
                        ]}
                    />
                </Form.Item>
                <Form.Item label="Field" name="field">
                    <Select style={{ width: 200 }} options={fieldOptions} />
                </Form.Item>
                <Form.Item label="Comparator" name="comparator">
                    <Select
                        style={{ width: 200 }}
                        options={[
                            { label: 'Equals', value: 'EQUALS' },
                            { label: 'Contains', value: 'CONTAINS' },
                            { label: 'Pattern', value: 'PATTERN' },
                            { label: 'All', value: 'ALL' }
                        ]}
                    />
                </Form.Item>
                <Form.Item label="Value" name="value">
                    <Input />
                </Form.Item>
                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
                        Add
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    )
}
